import { Box, Paper, Typography } from '@mui/material';
import { FacilityZoneYardChecks } from '../../api/yardCheck';
import { NEEDS_REVIEW_BG, NEEDS_REVIEW_COLOR, ageMinutes, formatAge, freshnessBucketFor } from './freshness';
import { formatYardSpotCardDetailLine } from './utils';
import { YardSpotSelection } from './YardChecksSpotGrid';

// This card's own cutoff — deliberately shorter than (and independent of) the
// backend's "not seen" freshness bucket (>4h), which still drives the summary
// card's counts and this card's per-box color coding.
const NOT_SEEN_CARD_MAX_MINUTES = 60;

interface YardChecksNotSeenCardProps {
  facilityZones: FacilityZoneYardChecks[];
  now: Date;
  onSelectSpot: (selection: YardSpotSelection) => void;
}

const YardChecksNotSeenCard = ({ facilityZones, now, onSelectSpot }: YardChecksNotSeenCardProps) => {
  const notSeen: YardSpotSelection[] = facilityZones
    .flatMap(zone => zone.yardSpots.map(spot => ({ zone, spot })))
    .filter(({ spot }) => {
      const age = ageMinutes(spot.latestHistory?.timestamp, now);
      return age === null || age > NOT_SEEN_CARD_MAX_MINUTES;
    })
    // Oldest first; spots with no history at all (age === null, i.e. "never") sort ahead of
    // anything with a real timestamp, since they're the most overdue by definition.
    .sort((a, b) => {
      const ageA = ageMinutes(a.spot.latestHistory?.timestamp, now);
      const ageB = ageMinutes(b.spot.latestHistory?.timestamp, now);
      if (ageA === null && ageB === null) return 0;
      if (ageA === null) return -1;
      if (ageB === null) return 1;
      return ageB - ageA;
    });

  return (
    <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography
          variant="overline"
          sx={{ color: 'text.secondary', fontWeight: 500, letterSpacing: '0.08em' }}
        >
          Not seen in the last hour
        </Typography>
        <Box
          sx={{
            fontSize: 12,
            fontWeight: 500,
            px: 1,
            py: 0.25,
            borderRadius: 5,
            bgcolor: '#fdeded',
            color: '#5f2120',
          }}
        >
          {notSeen.length} spots
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(168px, 1fr))',
          gap: 1,
          mt: 2,
        }}
      >
        {notSeen.map(({ zone, spot }) => {
          const bucket = freshnessBucketFor(spot.latestHistory?.timestamp, now);
          const needsReview = Boolean(spot.latestHistory?.needsReview);
          return (
            <Box
              key={spot.yardSpotUuid}
              component="button"
              onClick={() => onSelectSpot({ zone, spot })}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.375,
                px: 1.25,
                py: 1,
                borderRadius: 1,
                border: 1,
                borderLeft: 4,
                borderColor: needsReview ? NEEDS_REVIEW_COLOR : 'divider',
                borderLeftColor: needsReview ? NEEDS_REVIEW_COLOR : bucket.color,
                bgcolor: needsReview ? NEEDS_REVIEW_BG : bucket.bg,
                cursor: 'pointer',
                textAlign: 'left',
                font: 'inherit',
                '&:hover': { boxShadow: 2 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{spot.yardSpotName}</Typography>
                <Typography
                  sx={{ ml: 'auto', fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}
                >
                  {formatAge(spot.latestHistory?.timestamp, now)}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: 12,
                  lineHeight: 1.35,
                  color: 'text.secondary',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {formatYardSpotCardDetailLine(spot.latestHistory)}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Typography variant="caption" sx={{ color: 'text.secondary', mt: 'auto', pt: 2, lineHeight: 1.6 }}>
        Ordered oldest first. Click a spot for details — same as clicking it in the grid below.
      </Typography>
    </Paper>
  );
};

export default YardChecksNotSeenCard;
